import { cn } from '@/lib/utils';
import { Loader2, Trash2 } from 'lucide-react';
import React from 'react';

import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { Button } from '@/components/ui/button';
import { FluidHoverGroup } from '@/components/ui/fluid-hover-group';
import { FluidHoverDivRow } from '@/components/ui/fluid-hover-row';
import { Switch } from '@/components/ui/switch';
import { SubtleTabsList } from '@/components/ui/tabs-subtle';

interface ToggleSectionProps {
    title: string;
    description?: string;
    emptyText?: string;
    isEmpty?: boolean;
    children: React.ReactNode;
}

export const ToggleSection: React.FC<ToggleSectionProps> = ({
    title,
    description,
    emptyText,
    isEmpty = false,
    children
}) => (
    <section className="space-y-2">
        <div>
            <h4 className="font-medium text-foreground text-subtitle">
                {title}
            </h4>
            {description && (
                <p className="text-caption text-muted-foreground">
                    {description}
                </p>
            )}
        </div>
        {isEmpty ? (
            <p className="py-3 text-caption text-muted-foreground">
                {emptyText}
            </p>
        ) : (
            <FluidHoverGroup className="divide-y divide-border">
                {children}
            </FluidHoverGroup>
        )}
    </section>
);

interface ToggleRowProps {
    id: string;
    title: string;
    description?: string;
    checked: boolean;
    onCheckedChange?: (checked: boolean) => void;
    disabled?: boolean;
    extra?: React.ReactNode;
}

export const ToggleRow: React.FC<ToggleRowProps> = ({
    id,
    title,
    description,
    checked,
    onCheckedChange,
    disabled = false,
    extra
}) => (
    <FluidHoverDivRow className="flex justify-between items-center gap-4 px-3 py-3">
        <label
            htmlFor={id}
            className={cn(
                'min-w-0 after:absolute after:inset-0',
                disabled ? 'cursor-default' : 'cursor-pointer'
            )}
        >
            <span className="block font-medium text-foreground text-subtitle truncate">
                {title}
            </span>
            {description && (
                <span className="block text-caption text-muted-foreground">
                    {description}
                </span>
            )}
        </label>
        <div className="z-10 relative flex items-center gap-3 shrink-0">
            {extra}
            <Switch
                id={id}
                checked={checked}
                onCheckedChange={onCheckedChange}
                disabled={disabled}
            />
        </div>
    </FluidHoverDivRow>
);

interface AutoConfirmControlProps {
    auto: boolean;
    onChange: (auto: boolean) => void;
}

const AUTO_CONFIRM_TABS = [
    { value: 'auto', label: 'Auto' },
    { value: 'confirm', label: 'Confirm' }
];

export const AutoConfirmControl: React.FC<AutoConfirmControlProps> = ({
    auto,
    onChange
}) => (
    <SubtleTabsList
        tabs={AUTO_CONFIRM_TABS}
        value={auto ? 'auto' : 'confirm'}
        onValueChange={(value) => onChange(value === 'auto')}
    />
);

interface SkillItem {
    skill_id: string;
    display_title: string;
    source?: string;
}

interface SkillToggleRowsProps {
    skills: SkillItem[];
    selectedIds: string[];
    onToggle: (skillId: string) => void;
    deletingSkillId: string | null;
    skillToDelete: string | null;
    onSkillToDeleteChange: (skillId: string | null) => void;
    onDelete: (skillId: string) => void;
}

export const SkillToggleRows: React.FC<SkillToggleRowsProps> = ({
    skills,
    selectedIds,
    onToggle,
    deletingSkillId,
    skillToDelete,
    onSkillToDeleteChange,
    onDelete
}) => (
    <ToggleSection
        title="Skills"
        isEmpty={skills.length === 0}
        emptyText="No skills available"
    >
        {skills.map((skill) => {
            const isCustom = skill.source !== 'anthropic';
            const isDeleting = deletingSkillId === skill.skill_id;
            return (
                <ToggleRow
                    key={skill.skill_id}
                    id={`skill-${skill.skill_id}`}
                    title={skill.display_title}
                    description={isCustom ? 'Custom skill' : 'Anthropic skill'}
                    checked={selectedIds.includes(skill.skill_id)}
                    onCheckedChange={() => onToggle(skill.skill_id)}
                    extra={
                        isCustom && (
                            <>
                                <Button
                                    type="button"
                                    variant="ghost-destructive"
                                    size="icon-compact"
                                    onClick={() =>
                                        onSkillToDeleteChange(skill.skill_id)
                                    }
                                    disabled={isDeleting}
                                    title="Delete custom skill"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="animate-spin" />
                                    ) : (
                                        <Trash2 />
                                    )}
                                </Button>
                                <ConfirmDeleteDialog
                                    open={skillToDelete === skill.skill_id}
                                    onOpenChange={(open) =>
                                        !open && onSkillToDeleteChange(null)
                                    }
                                    title="Delete custom skill"
                                    description={
                                        <>
                                            This will permanently delete{' '}
                                            <span className="font-medium text-foreground">
                                                {skill.display_title}
                                            </span>{' '}
                                            for everyone, including from
                                            Anthropic. This action cannot be
                                            undone.
                                        </>
                                    }
                                    onConfirm={() => {
                                        onSkillToDeleteChange(null);
                                        onDelete(skill.skill_id);
                                    }}
                                />
                            </>
                        )
                    }
                />
            );
        })}
    </ToggleSection>
);
